import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LeadsAPI, ShowcaseAPI } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import GoldenParticles from "@/components/GoldenParticles";
import {
  Trophy,
  ShieldCheck,
  User as UserIcon,
  ScanLine,
  Sparkles,
  CheckCircle2,
  ArrowRight,
  Wifi,
  Layers,
  PenTool,
  Crown,
  Medal,
  Pencil,
  QrCode,
  Smartphone,
  ChevronDown,
} from "lucide-react";

const CARD_ICONS = {
  user: UserIcon,
  trophy: Trophy,
  shield: ShieldCheck,
  crown: Crown,
  medal: Medal,
  sparkles: Sparkles,
  scan: ScanLine,
  wifi: Wifi,
  layers: Layers,
  pen: PenTool,
  check: CheckCircle2,
};

const MEDAL_PLAYER =
  "https://customer-assets.emergentagent.com/job_player-pages/artifacts/glgn8cgk_2B550BBE-D7D9-4567-B498-ADC18B49F117.png";

const SPORTS = [
  "Football",
  "Basketball",
  "Tennis",
  "Padel",
  "Hockey",
  "Rugby",
  "Arts martiaux",
  "Athlétisme",
  "Voile",
  "Ski",
  "Cyclisme",
  "Natation",
  "Golf",
  "Autre",
];

const LEAD_PRODUCTS = [
  { key: "player", title: "Médaille Joueur", accent: "#0066FF" },
  { key: "tournament", title: "Coupe Tournoi", accent: "#D4AF37" },
  { key: "team", title: "Plaque Équipe", accent: "#C0C0C0" },
];

const GOLD_GRADIENT =
  "linear-gradient(180deg,#FFE9A8 0%,#D4AF37 60%,#B8941F 100%)";

const easeReveal = [0.22, 1, 0.36, 1];
const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, ease: easeReveal },
};

export default function Showcase() {
  const { user } = useAuth();
  const [cards, setCards] = useState([]);
  const [content, setContent] = useState(null);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    club_name: "",
    role: "",
    sports: [],
    products: [],
    quantity_estimate: "",
    message: "",
  });
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    ShowcaseAPI.get()
      .then((d) => setCards(d.cards || []))
      .catch(() => {});
    ShowcaseAPI.getContent()
      .then((d) => setContent(d.content))
      .catch(() => {});
  }, []);

  const toggle = (key, value) =>
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(value)
        ? f[key].filter((x) => x !== value)
        : [...f[key], value],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim()) {
      toast.error("Nom et email requis");
      return;
    }
    setSaving(true);
    try {
      await LeadsAPI.submit(form);
      setSubmitted(true);
      toast.success("Demande envoyée — on revient vers toi sous 24h");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setSaving(false);
    }
  };

  if (!content) return <div className="min-h-screen bg-[#050505]" />;
  const { hero, products, steps, multisport, benefits, faq, contact } = content;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Top bar minimal */}
      <header className="absolute top-0 inset-x-0 z-30 px-5 sm:px-10 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img
            src="/laser-tech-logo.png"
            alt="Laser Tech Création"
            className="h-10 w-10 object-contain"
          />
          <span className="font-display text-sm uppercase tracking-[0.2em] font-bold leading-none">
            Laser Tech
            <span className="block text-[9px] font-normal tracking-[0.25em] text-zinc-400 mt-1">
              Création · Genève
            </span>
          </span>
        </div>
        <a
          href="#contact"
          data-testid="showcase-top-cta"
          className="ltc-cta-gold hidden sm:inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-bold text-black px-5 py-2.5 rounded-full"
          style={{ background: GOLD_GRADIENT }}
        >
          {hero.cta_primary} <ArrowRight className="w-3 h-3" />
        </a>
      </header>

      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            background: `radial-gradient(ellipse at 75% 25%, rgba(212,175,55,0.18) 0%, transparent 55%), radial-gradient(ellipse at 15% 85%, rgba(212,175,55,0.05) 0%, transparent 55%), linear-gradient(180deg, #050505 0%, #0a0a0d 100%)`,
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none opacity-50"
          style={{
            background:
              "repeating-linear-gradient(115deg, transparent 0 80px, rgba(212,175,55,0.03) 80px 81px)",
          }}
        />
        <GoldenParticles count={18} density="normal" />
        <div className="relative max-w-6xl mx-auto px-5 sm:px-10 pt-28 sm:pt-32 pb-16 sm:pb-20 w-full grid lg:grid-cols-[1.2fr,1fr] gap-10 lg:gap-12 items-center">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: easeReveal }}
              className="flex items-center gap-3 mb-5 sm:mb-6"
            >
              <span className="ltc-hairline w-8 sm:w-12 shrink-0" />
              <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37]">
                {hero.overline}
              </span>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.08, ease: easeReveal }}
              className="font-display text-[2.6rem] leading-[0.95] sm:text-6xl md:text-7xl font-semibold uppercase tracking-tight sm:leading-[0.92]"
              data-testid="showcase-hero-title"
            >
              {hero.title_line1}
              <br />
              <span className="text-gradient-gold">{hero.title_line2}</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.18, ease: easeReveal }}
              className="text-zinc-300 font-body text-base sm:text-lg leading-relaxed mt-5 sm:mt-6 max-w-xl"
            >
              {hero.subtitle}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.28, ease: easeReveal }}
              className="flex flex-wrap gap-3 mt-8 sm:mt-9"
            >
              <a
                href="#contact"
                data-testid="showcase-hero-cta"
                className="ltc-cta-gold inline-flex items-center justify-center gap-2 text-black font-bold uppercase tracking-[0.18em] text-xs px-6 min-h-[48px] rounded-full w-full sm:w-auto"
                style={{ background: GOLD_GRADIENT }}
              >
                {hero.cta_primary} <ArrowRight className="w-3 h-3" />
              </a>
              <a
                href="#products"
                data-testid="showcase-hero-explore"
                className="inline-flex items-center justify-center gap-2 text-white text-xs uppercase tracking-[0.18em] font-bold px-6 min-h-[48px] rounded-full border border-white/20 hover:bg-white/5 hover:border-[#D4AF37]/40 transition-colors w-full sm:w-auto"
              >
                {hero.cta_secondary}
              </a>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.45 }}
              className="flex flex-wrap items-center gap-x-6 gap-y-2.5 mt-10 sm:mt-12 text-zinc-500 text-[11px] uppercase tracking-[0.2em]"
            >
              {(hero.badges || []).filter(Boolean).map((b) => (
                <span key={b} className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-3 h-3 text-[#D4AF37]" /> {b}
                </span>
              ))}
            </motion.div>
          </div>

          {/* Visuel produit — visible aussi sur mobile */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.25, ease: easeReveal }}
            className="flex justify-center items-center"
          >
            <div
              className="ltc-float relative aspect-square w-full max-w-[280px] sm:max-w-sm lg:max-w-md rounded-3xl flex items-center justify-center overflow-hidden"
              style={{
                background:
                  "radial-gradient(ellipse at center, rgba(212,175,55,0.25), rgba(212,175,55,0.05) 50%, transparent 75%), #0a0a0d",
                border: "1px solid rgba(212,175,55,0.25)",
                boxShadow:
                  "0 30px 80px -30px rgba(212,175,55,0.35), inset 0 0 0 1px rgba(212,175,55,0.05)",
              }}
            >
              <img
                src={hero.side_image_url || "/laser-tech-logo.png"}
                alt="Laser Tech Création"
                className={
                  hero.side_image_url
                    ? "w-full h-full object-cover"
                    : "w-2/3 h-2/3 object-contain"
                }
              />
              <div className="ltc-scan-beam" />
              <div className="absolute bottom-5 sm:bottom-6 left-0 right-0 text-center text-[9px] sm:text-[10px] uppercase tracking-[0.35em] text-[#D4AF37]/80 font-bold">
                {hero.side_caption}
              </div>
            </div>
          </motion.div>
        </div>
        <a
          href="#products"
          aria-label="Découvrir"
          className="absolute bottom-5 left-1/2 -translate-x-1/2 text-[#D4AF37]/60 hover:text-[#D4AF37] transition-colors hidden sm:block"
        >
          <ChevronDown className="w-5 h-5 animate-bounce" />
        </a>
      </section>

      {/* 3 PRODUITS */}
      <section
        id="products"
        className="max-w-6xl mx-auto px-5 sm:px-10 py-16 sm:py-24 scroll-mt-16"
        data-testid="showcase-products"
      >
        <motion.div {...reveal} className="text-center max-w-2xl mx-auto mb-12 sm:mb-14">
          <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">
            {products.overline}
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold uppercase tracking-tight">
            {products.title}
          </h2>
          <p className="text-zinc-400 font-body mt-4">{products.subtitle}</p>
          {user?.role === "admin" && (
            <Link
              to="/admin/showcase"
              data-testid="showcase-edit-cards-link"
              className="inline-flex items-center gap-2 mt-5 text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-400 hover:text-white border border-white/15 bg-white/[0.04] rounded-full px-3 py-1.5"
            >
              <Pencil className="w-3 h-3" /> Éditer ces cartes
            </Link>
          )}
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {cards.map((p, ci) => {
            const CardIcon = CARD_ICONS[p.icon] || Medal;
            const isExternal = /^https?:\/\//.test(p.link_url || "");
            return (
              <motion.div
                key={p.id || ci}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                whileHover={{ y: -6 }}
                transition={{
                  duration: 0.6,
                  delay: ci * 0.12,
                  ease: easeReveal,
                  y: { type: "spring", stiffness: 200 },
                }}
                data-testid={`product-card-${ci}`}
                className="metal-card rounded-2xl flex flex-col overflow-hidden group"
                style={{ "--accent": p.accent }}
              >
                {/* Filet accent distinctif */}
                <div
                  className="h-[2px] w-full"
                  style={{
                    background: `linear-gradient(90deg, transparent, ${p.accent}, transparent)`,
                  }}
                />
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-start justify-between mb-5">
                    <span
                      className="font-display text-[11px] font-bold tracking-[0.3em]"
                      style={{ color: `${p.accent}99` }}
                    >
                      {String(ci + 1).padStart(2, "0")}
                    </span>
                    <span
                      className="inline-flex items-center gap-1.5 text-[9px] uppercase tracking-[0.2em] font-bold rounded-full px-2.5 py-1"
                      style={{
                        color: p.accent,
                        border: `1px solid ${p.accent}35`,
                        background: `${p.accent}0d`,
                      }}
                    >
                      <QrCode className="w-3 h-3" /> QR + NFC
                    </span>
                  </div>
                  {p.image_url ? (
                    <div className="w-full aspect-[16/9] rounded-xl overflow-hidden mb-5 border border-white/10 relative">
                      <img
                        src={p.image_url}
                        alt={p.title}
                        className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                      />
                      <div
                        className="absolute inset-0 pointer-events-none"
                        style={{
                          background:
                            "linear-gradient(180deg, transparent 55%, rgba(5,5,5,0.55) 100%)",
                        }}
                      />
                    </div>
                  ) : (
                    <div
                      className="w-full aspect-[16/9] rounded-xl mb-5 flex items-center justify-center relative overflow-hidden"
                      style={{
                        background: `radial-gradient(ellipse at center, ${p.accent}1a, transparent 70%), rgba(255,255,255,0.02)`,
                        border: `1px solid ${p.accent}25`,
                      }}
                    >
                      <div
                        className="w-14 h-14 rounded-2xl flex items-center justify-center"
                        style={{
                          background: `${p.accent}15`,
                          border: `1px solid ${p.accent}40`,
                          boxShadow: `0 0 36px ${p.accent}25`,
                        }}
                      >
                        <CardIcon className="w-6 h-6" style={{ color: p.accent }} />
                      </div>
                    </div>
                  )}
                  <h3 className="font-display text-2xl font-semibold uppercase tracking-tight">
                    {p.title}
                  </h3>
                  <p className="text-zinc-400 text-sm font-body mt-2 mb-5">
                    {p.description}
                  </p>
                  <ul className="space-y-2.5 mb-6 flex-1">
                    {(p.bullets || []).filter(Boolean).map((b, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2 text-zinc-300 text-sm font-body"
                      >
                        <CheckCircle2
                          className="w-3.5 h-3.5 mt-0.5 shrink-0"
                          style={{ color: p.accent }}
                        />
                        {b}
                      </li>
                    ))}
                  </ul>
                  {p.link_url ? (
                    isExternal ? (
                      <a
                        href={p.link_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`product-demo-${ci}`}
                        className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-bold border-t border-white/10 pt-4 min-h-[44px] hover:text-white text-zinc-400 transition-colors"
                      >
                        {p.button_label}{" "}
                        <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-1" />
                      </a>
                    ) : (
                      <Link
                        to={p.link_url}
                        data-testid={`product-demo-${ci}`}
                        className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-bold border-t border-white/10 pt-4 min-h-[44px] hover:text-white text-zinc-400 transition-colors"
                      >
                        {p.button_label}{" "}
                        <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-1" />
                      </Link>
                    )
                  ) : (
                    <span className="border-t border-white/10 pt-4" />
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section
        className="relative py-16 sm:py-24"
        style={{
          background:
            "linear-gradient(180deg, #050505 0%, #0a0a0c 50%, #050505 100%)",
        }}
      >
        <div className="max-w-6xl mx-auto px-5 sm:px-10">
          <div className="grid md:grid-cols-[1fr,1.2fr] gap-12 items-center">
            <motion.div {...reveal}>
              <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">
                {steps.overline}
              </div>
              <h2 className="font-display text-4xl sm:text-5xl font-semibold uppercase tracking-tight mb-8">
                {steps.title}
              </h2>
              <div className="relative">
                <div
                  className="absolute left-5 top-5 bottom-5 w-px"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(212,175,55,0.4), rgba(212,175,55,0.05))",
                  }}
                />
                <div className="space-y-7">
                  {(steps.items || []).map((s, si) => {
                    const StepIcon = CARD_ICONS[s.icon] || Sparkles;
                    return (
                      <motion.div
                        key={s.title}
                        initial={{ opacity: 0, x: -16 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.55, delay: si * 0.1, ease: easeReveal }}
                        className="flex gap-4 relative"
                      >
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#0a0a0c] border border-[#D4AF37]/30 shrink-0 relative z-10 glow-gold-soft">
                          <StepIcon className="w-4 h-4 text-[#D4AF37]" />
                        </div>
                        <div>
                          <div className="font-display font-bold uppercase tracking-tight">
                            {s.title}
                          </div>
                          <div className="text-zinc-400 text-sm font-body mt-1">
                            {s.text}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
            <motion.div {...reveal} className="relative">
              <div
                className="metal-card rounded-3xl p-3 sm:p-4 overflow-hidden relative"
                style={{
                  background:
                    "radial-gradient(ellipse at center, rgba(212,175,55,0.18), transparent 70%), #0a0a0c",
                  boxShadow:
                    "0 30px 80px -30px rgba(212,175,55,0.35), inset 0 0 0 1px rgba(212,175,55,0.08)",
                }}
              >
                <div className="relative rounded-2xl overflow-hidden">
                  <img
                    src={steps.image_url || MEDAL_PLAYER}
                    alt="Laser Tech — La coupe qui récompense l'excellence"
                    className="w-full h-auto object-contain"
                  />
                  <div className="ltc-scan-beam" />
                </div>
              </div>

              {/* Démo visuelle : récompense physique → page numérique */}
              <div
                className="mt-4 rounded-2xl border border-[#D4AF37]/20 bg-[#0a0a0c]/80 backdrop-blur px-4 sm:px-6 py-4 flex items-center gap-3 sm:gap-4"
                data-testid="showcase-nfc-demo"
              >
                <div className="flex flex-col items-center gap-1.5 shrink-0">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center bg-[#D4AF37]/10 border border-[#D4AF37]/35">
                    <Medal className="w-4 h-4 text-[#D4AF37]" />
                  </div>
                  <span className="text-[8px] uppercase tracking-[0.18em] text-zinc-500 font-bold">
                    Récompense
                  </span>
                </div>
                <div className="ltc-flow-line" />
                <div className="flex flex-col items-center gap-1.5 shrink-0">
                  <div className="relative w-10 h-10 rounded-full flex items-center justify-center bg-[#D4AF37]/15 border border-[#D4AF37]/50">
                    <span className="ltc-pulse-ring" />
                    <span className="ltc-pulse-ring delay" />
                    <QrCode className="w-4 h-4 text-[#D4AF37]" />
                  </div>
                  <span className="text-[8px] uppercase tracking-[0.18em] text-[#D4AF37] font-bold">
                    Scan QR / NFC
                  </span>
                </div>
                <div className="ltc-flow-line" />
                <div className="flex flex-col items-center gap-1.5 shrink-0">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center bg-[#D4AF37]/10 border border-[#D4AF37]/35">
                    <Smartphone className="w-4 h-4 text-[#D4AF37]" />
                  </div>
                  <span className="text-[8px] uppercase tracking-[0.18em] text-zinc-500 font-bold">
                    Page numérique
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Multisport tagline */}
      <section className="max-w-3xl mx-auto px-5 sm:px-10 py-14 sm:py-16 text-center">
        <motion.div {...reveal}>
          <div className="ltc-hairline w-16 mx-auto mb-8" />
          <h2 className="font-display text-3xl sm:text-4xl font-semibold uppercase tracking-tight">
            {multisport.title}
          </h2>
          <p className="text-zinc-400 font-body mt-3">{multisport.text}</p>
        </motion.div>
      </section>

      {/* WHY US */}
      <section className="max-w-6xl mx-auto px-5 sm:px-10 py-16 sm:py-20">
        <div className="grid sm:grid-cols-3 gap-5">
          {(benefits.items || []).map((b, bi) => {
            const BenefitIcon = CARD_ICONS[b.icon] || Layers;
            return (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: bi * 0.1, ease: easeReveal }}
                className="metal-card rounded-2xl p-6"
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D4AF37]/10 border border-[#D4AF37]/30 mb-4">
                  <BenefitIcon className="w-4 h-4 text-[#D4AF37]" />
                </div>
                <h3 className="font-display text-lg font-bold uppercase tracking-tight">
                  {b.title}
                </h3>
                <p className="text-zinc-400 text-sm font-body mt-2">{b.text}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-5 sm:px-10 py-16 sm:py-20">
        <motion.h2
          {...reveal}
          className="font-display text-3xl sm:text-4xl font-semibold uppercase tracking-tight mb-10 text-center"
        >
          {faq.title}
        </motion.h2>
        <div className="space-y-3">
          {(faq.items || []).map((f) => (
            <details
              key={f.q}
              className="metal-card rounded-xl px-5 py-4 group"
              data-testid={`faq-${f.q.replace(/\s+/g, "-").slice(0, 20)}`}
            >
              <summary className="font-display font-bold uppercase tracking-tight cursor-pointer list-none flex justify-between items-center gap-4 text-sm sm:text-base min-h-[44px]">
                {f.q}
                <span className="w-7 h-7 rounded-full border border-white/10 flex items-center justify-center shrink-0 group-open:border-[#D4AF37]/40 transition-colors">
                  <ArrowRight className="w-3.5 h-3.5 text-zinc-500 group-open:rotate-90 group-open:text-[#D4AF37] transition-transform duration-300" />
                </span>
              </summary>
              <div className="ltc-faq-answer">
                <p className="text-zinc-400 text-sm font-body leading-relaxed">
                  {f.a}
                </p>
              </div>
            </details>
          ))}
        </div>
      </section>

      {/* CONTACT FORM */}
      <section
        id="contact"
        className="relative py-16 sm:py-24 scroll-mt-16"
        style={{
          background:
            "linear-gradient(180deg, #050505 0%, #0c0c10 50%, #050505 100%)",
        }}
        data-testid="showcase-contact"
      >
        <div className="max-w-3xl mx-auto px-5 sm:px-10">
          {submitted ? (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="metal-card rounded-2xl p-10 text-center"
              data-testid="showcase-success"
            >
              <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center bg-[#D4AF37]/10 border border-[#D4AF37]/30 mb-5">
                <CheckCircle2 className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <h2 className="font-display text-3xl font-semibold uppercase tracking-tight mb-3">
                Demande reçue
              </h2>
              <p className="text-zinc-400 font-body">
                Merci{form.full_name ? `, ${form.full_name.split(" ")[0]}` : ""}.
                On revient vers toi sous 24h ouvrées avec une maquette de tes
                médailles personnalisées.
              </p>
            </motion.div>
          ) : (
            <>
              <motion.div {...reveal} className="text-center mb-10">
                <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">
                  {contact.overline}
                </div>
                <h2 className="font-display text-4xl sm:text-5xl font-semibold uppercase tracking-tight">
                  {contact.title}
                </h2>
                <p className="text-zinc-400 font-body mt-3">
                  {contact.subtitle}
                </p>
              </motion.div>

              <form
                onSubmit={submit}
                className="metal-card rounded-2xl p-5 sm:p-8 space-y-5"
                data-testid="showcase-form"
              >
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-overline">Nom complet *</Label>
                    <Input
                      value={form.full_name}
                      onChange={(e) =>
                        setForm({ ...form, full_name: e.target.value })
                      }
                      data-testid="lead-name"
                      required
                      className="bg-white/[0.04] border-white/10 text-white h-11 focus-visible:ring-[#D4AF37]/40"
                    />
                  </div>
                  <div>
                    <Label className="text-overline">Email *</Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) =>
                        setForm({ ...form, email: e.target.value })
                      }
                      data-testid="lead-email"
                      required
                      className="bg-white/[0.04] border-white/10 text-white h-11 focus-visible:ring-[#D4AF37]/40"
                    />
                  </div>
                  <div>
                    <Label className="text-overline">Téléphone</Label>
                    <Input
                      value={form.phone}
                      onChange={(e) =>
                        setForm({ ...form, phone: e.target.value })
                      }
                      data-testid="lead-phone"
                      className="bg-white/[0.04] border-white/10 text-white h-11 focus-visible:ring-[#D4AF37]/40"
                    />
                  </div>
                  <div>
                    <Label className="text-overline">Club / Organisation</Label>
                    <Input
                      value={form.club_name}
                      onChange={(e) =>
                        setForm({ ...form, club_name: e.target.value })
                      }
                      data-testid="lead-club"
                      className="bg-white/[0.04] border-white/10 text-white h-11 focus-visible:ring-[#D4AF37]/40"
                    />
                  </div>
                  <div>
                    <Label className="text-overline">Ton rôle</Label>
                    <Input
                      placeholder="Président, coach, dirigeant…"
                      value={form.role}
                      onChange={(e) =>
                        setForm({ ...form, role: e.target.value })
                      }
                      data-testid="lead-role"
                      className="bg-white/[0.04] border-white/10 text-white h-11 focus-visible:ring-[#D4AF37]/40"
                    />
                  </div>
                  <div>
                    <Label className="text-overline">Quantité estimée</Label>
                    <Input
                      placeholder="50 médailles, 10 plaques…"
                      value={form.quantity_estimate}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          quantity_estimate: e.target.value,
                        })
                      }
                      data-testid="lead-qty"
                      className="bg-white/[0.04] border-white/10 text-white h-11 focus-visible:ring-[#D4AF37]/40"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-overline mb-2 block">
                    Produits qui t'intéressent
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {LEAD_PRODUCTS.map((p) => {
                      const active = form.products.includes(p.key);
                      return (
                        <button
                          key={p.key}
                          type="button"
                          onClick={() => toggle("products", p.key)}
                          data-testid={`lead-product-${p.key}`}
                          className="text-[11px] uppercase tracking-[0.18em] font-bold px-4 min-h-[44px] rounded-full border transition-all"
                          style={{
                            borderColor: active
                              ? p.accent
                              : "rgba(255,255,255,0.15)",
                            color: active ? "#000" : "#a1a1aa",
                            background: active
                              ? `linear-gradient(180deg, ${p.accent}, ${p.accent}cc)`
                              : "transparent",
                          }}
                        >
                          {p.title}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <Label className="text-overline mb-2 block">
                    Sport(s) principaux
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {SPORTS.map((s) => {
                      const active = form.sports.includes(s);
                      return (
                        <button
                          key={s}
                          type="button"
                          onClick={() => toggle("sports", s)}
                          className="text-[11px] uppercase tracking-[0.18em] font-medium px-3.5 min-h-[40px] rounded-full border transition-all"
                          style={{
                            borderColor: active
                              ? "#D4AF37"
                              : "rgba(255,255,255,0.12)",
                            color: active ? "#fff" : "#71717a",
                            background: active
                              ? "rgba(212,175,55,0.12)"
                              : "transparent",
                          }}
                        >
                          {s}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <Label className="text-overline">Message</Label>
                  <Textarea
                    value={form.message}
                    onChange={(e) =>
                      setForm({ ...form, message: e.target.value })
                    }
                    placeholder="Parle-nous de ton projet, ton timing, tes besoins spécifiques…"
                    rows={4}
                    data-testid="lead-message"
                    className="bg-white/[0.04] border-white/10 text-white focus-visible:ring-[#D4AF37]/40"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={saving}
                  data-testid="lead-submit"
                  className="ltc-cta-gold text-black font-bold uppercase tracking-[0.18em] text-xs h-12 px-8 w-full sm:w-auto rounded-full"
                  style={{ background: GOLD_GRADIENT }}
                >
                  {saving ? "Envoi…" : "Envoyer ma demande"}
                </Button>
                <p className="text-zinc-500 text-[11px] font-body">
                  Tes données restent confidentielles. Réponse sous 24h
                  ouvrées.
                </p>
              </form>
            </>
          )}
        </div>
      </section>

      <footer className="border-t border-white/5 py-10 text-center pb-mobile-safe md:pb-10">
        <img
          src="/laser-tech-logo.png"
          alt=""
          className="h-8 w-8 object-contain mx-auto mb-3 opacity-70"
        />
        <div className="font-display text-xs uppercase tracking-[0.25em] text-zinc-500">
          Laser Tech Création · Genève · Médailles · Coupes · Plaques
          connectées
        </div>
      </footer>
    </div>
  );
}
